import { useState, useEffect } from "react";
import axios from "axios";
import { Snowflake, LogOut, Loader2, Mail, MapPin, Calendar, ImageIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "hp_admin_token";
const inputClass = "h-12 rounded-xl border-slate-300 bg-white text-base focus:border-blue-500 focus:ring-2 focus:ring-blue-500";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onLogin(data.access_token);
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="admin-login-form">
        <div className="mb-6 flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600 text-white"><Snowflake className="h-5 w-5" /></div>
          <span className="font-heading text-xl font-bold text-slate-950">Admin Login</span>
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} placeholder="admin@heatpumphq.com" data-testid="admin-email-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} placeholder="••••••••" data-testid="admin-password-input" />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
          <Button type="submit" disabled={loading} className="h-12 w-full rounded-full bg-blue-600 text-base font-medium text-white hover:bg-blue-700" data-testid="admin-login-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function PhotoModal({ token, photo, onClose }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    setUrl(`${API}/files/${photo.storage_path}?auth=${token}`);
  }, [photo, token]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" onClick={onClose} data-testid="photo-modal">
      <div className="relative max-h-[85vh] max-w-3xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -right-3 -top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white text-slate-900 shadow" data-testid="photo-modal-close"><X className="h-5 w-5" /></button>
        <img src={url} alt={photo.original_filename} className="max-h-[85vh] rounded-xl object-contain" />
      </div>
    </div>
  );
}

function LeadCard({ lead, token, onView }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="lead-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-950">{lead.first_name} {lead.last_name}</h3>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
            <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {lead.email}</span>
            <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {lead.zip_code}</span>
            <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {new Date(lead.created_at).toLocaleString()}</span>
          </div>
        </div>
        <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">{lead.timeline}</span>
      </div>
      <div className="mt-4 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        {[
          ["Address", lead.property_address], ["Square Footage", lead.square_footage],
          ["Bedrooms", lead.bedrooms], ["Additional Rooms", lead.additional_rooms],
          ["Floors", lead.floors], ["Indoor Units", lead.indoor_units],
          ["Current System", lead.current_system], ["Equipment Model", lead.equipment_model],
          ["Utility Provider", lead.utility_provider],
        ].filter(([, v]) => v).map(([k, v]) => (
          <div key={k}><span className="font-medium text-slate-500">{k}: </span><span className="text-slate-800">{v}</span></div>
        ))}
      </div>
      {lead.notes && (
        <div className="mt-4 rounded-xl bg-blue-50 p-4 text-sm" data-testid="lead-notes">
          <span className="font-medium text-blue-700">Notes / Questions: </span>
          <span className="text-slate-800">{lead.notes}</span>
        </div>
      )}
      {lead.photos?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {lead.photos.map((p) => (
            <button key={p.id} onClick={() => onView(p)} className="flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100" data-testid="lead-photo-thumb">
              <ImageIcon className="h-3.5 w-3.5" /> {p.original_filename.slice(0, 18)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewPhoto, setViewPhoto] = useState(null);

  const fetchLeads = async (t) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads`, { headers: { Authorization: `Bearer ${t}` } });
      setLeads(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error("Failed to load leads.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchLeads(token);
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setLeads([]);
  };

  if (!token) return <Login onLogin={setToken} />;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600 text-white"><Snowflake className="h-5 w-5" /></div>
            <span className="font-heading text-lg font-bold text-slate-950">Leads Dashboard</span>
          </div>
          <Button onClick={logout} variant="outline" className="rounded-full border-slate-300 hover:bg-slate-100" data-testid="admin-logout"><LogOut className="mr-2 h-4 w-4" /> Logout</Button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="font-heading text-2xl font-bold text-slate-950">Captured Leads</h1>
          <span className="rounded-full bg-emerald-50 px-4 py-1.5 text-sm font-semibold text-emerald-700" data-testid="lead-count">{leads.length} total</span>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
        ) : leads.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-20 text-center text-slate-500" data-testid="empty-state">No leads yet. Submissions will appear here.</div>
        ) : (
          <div className="grid gap-4">
            {leads.map((lead) => (
              <LeadCard key={lead.id} lead={lead} token={token} onView={setViewPhoto} />
            ))}
          </div>
        )}
      </main>

      {viewPhoto && <PhotoModal token={token} photo={viewPhoto} onClose={() => setViewPhoto(null)} />}
    </div>
  );
}
